import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '@/api/client';
import { inventory, type InventoryRowDTO } from '@/api/endpoints';
import { Icon } from '@/components/ui/Icon';
import { useRun } from '@/store/run';
import { useSession } from '@/store/session';
import { ItemGrid } from './ItemGrid';

export function Bag() {
  const [rows, setRows] = useState<InventoryRowDTO[] | null>(null);
  const [slots, setSlots] = useState({ used: 0, max: 40 });
  const [pool, setPool] = useState<'trash' | 'treasure'>('trash');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const applyWallet = useSession((s) => s.applyWallet);

  const load = () => void inventory.list().then((r) => {
    setRows(r.items);
    setSlots({ used: r.used_slots, max: r.max_slots });
  });

  useEffect(load, []);

  const held = (rows ?? []).filter((r) => r.pool === pool && r.quantity > 0);
  const sellable = (rows ?? [])
    .filter((r) => r.pool === 'trash' && r.quantity > 0)
    .reduce((sum, r) => sum + r.sell_value * r.quantity, 0);

  async function sellAll() {
    setBusy(true);
    setNote(null);
    try {
      let res = await inventory.sellAllTrash();
      applyWallet(res.wallet);
      setNote(res.sold > 0
        ? `Sold ${res.sold} item${res.sold === 1 ? '' : 's'} for `
          + `${res.coins_gained.toLocaleString('en-US')} coins.`
        : 'Nothing to sell.');
      load();
    } catch (e) {
      console.error('[Bag] sellAll failed:', e);
      if (e instanceof ApiError && e.code === 'MUST_BE_AT_SURFACE') {
        try {
          await useRun.getState().exit();
          const res = await inventory.sellAllTrash();
          applyWallet(res.wallet);
          setNote(res.sold > 0
            ? `Sold ${res.sold} item${res.sold === 1 ? '' : 's'} for `
              + `${res.coins_gained.toLocaleString('en-US')} coins.`
            : 'Nothing to sell.');
          load();
          return;
        } catch (retryErr) {
          console.error('[Bag] Retry sellAll after exit failed:', retryErr);
        }
      }
      setNote(e instanceof ApiError ? e.message : 'Could not sell that. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full overflow-y-auto px-3 pb-6 pt-3.5">
      <h2 className="font-pixel text-[9px] text-torch-core">HAUL</h2>
      <div className="mt-2.5 grid grid-cols-2 gap-1.5">
        <Stat k="Items held" v={`${slots.used} / ${slots.max}`} />
        <Stat k="Trash value" v={<span className="flex items-center gap-1"><Icon name="coin" size={14} className="text-coin" /> {sellable.toLocaleString('en-US')}</span>} />
      </div>

      <button
        type="button"
        onClick={() => void sellAll()}
        disabled={busy || sellable === 0}
        className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-sm border border-ui-lit
                   bg-[linear-gradient(#3A2718,var(--cave-floor))] py-3 font-bold
                   disabled:opacity-40"
      >
        <Icon name="coin" size={16} className="text-coin" />
        <span>{busy ? 'Selling…' : `Sell all trash · ${sellable.toLocaleString('en-US')}`}</span>
      </button>
      {note && (
        <p role="status"
           className="mt-2 rounded-md border border-[var(--hairline)] bg-black/40
                      px-2.5 py-1.5 text-[11px] text-ink">
          {note}
        </p>
      )}
      <p className="mt-2 text-[11px] text-ink-dim">
        Treasure stays in your bag and counts toward your collection score.
        You can only sell at the surface — there are no buyers underground.
      </p>

      <div className="mt-3 flex gap-1.5">
        {(['trash', 'treasure'] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPool(p)}
            className={`flex-1 rounded-lg border py-2 font-pixel text-[8px] uppercase ${
              pool === p
                ? 'border-coin bg-[linear-gradient(#F5D06A,#D0A420)] text-[#101010]'
                : 'border-[var(--hairline)] bg-black/30 text-ink-dim'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="mt-2.5">
        {rows == null ? <Skeleton /> : <ItemGrid items={held} empty="Nothing yet. Crack some rocks." />}
      </div>
    </div>
  );
}

function Stat({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="rounded-lg bg-black/30 px-2.5 py-2 text-[11px]">
      <p className="text-ink-dim">{k}</p>
      <div className="font-bold tabular text-ink-hi">{v}</div>
    </div>
  );
}

function Skeleton() {
  return <p className="py-6 text-center text-[11px] text-ink-dim">Loading…</p>;
}
